import React from 'react';
import { motion } from 'motion/react';
import { cn } from '../../../infrastructure/lib/utils';

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color?: string; // Icon color class, e.g. 'text-primary'
  change?: string;
  progress?: number;
  colorTheme?: 'primary' | 'tertiary-container' | 'electric-violet';
  delay?: number;
}

export function StatCard({
  label,
  value,
  icon: Icon,
  color = 'text-primary',
  change,
  progress,
  colorTheme = 'primary',
  delay = 0,
}: StatCardProps) {
  const isHighProgress = progress !== undefined && progress > 80;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="glass-panel p-6 rounded-2xl relative overflow-hidden group hover:border-primary/40 transition-all"
    >
      <div className="absolute -top-2 -right-2 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
        <Icon className={cn("w-16 h-16", color)} />
      </div>
      
      <p className="text-[10px] text-on-surface-variant uppercase font-bold tracking-widest mb-2 leading-none">
        {label}
      </p>
      
      <div className="flex items-baseline gap-2">
        <h3 className="font-display text-3xl font-bold text-on-surface leading-none">
          {value}
        </h3>
        {change && (
          <span className={cn("text-[10px] font-bold leading-none", isHighProgress ? "text-primary" : "text-tertiary-container")}>
            {change}
          </span>
        )}
      </div>

      {progress !== undefined && (
        <div className="mt-4 h-1 w-full bg-surface-container-highest rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 1, delay: 0.5 + delay }}
            className={cn(
              "h-full",
              colorTheme === 'primary' && "bg-primary",
              colorTheme === 'tertiary-container' && "bg-tertiary-container",
              colorTheme === 'electric-violet' && "bg-electric-violet"
            )}
          />
        </div>
      )}
    </motion.div>
  );
}
